import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/base/PageHeader";
import { StatusBadge } from "@/components/base/StatusBadge";
import { TableSkeleton } from "@/components/base/TableSkeleton";
import { EmptyState } from "@/components/base/EmptyState";
import Pagination from "@/components/base/Pagination";
import { fmtDateTime, statusLabel } from "@/lib/format";
import { downloadCsv, toCsv } from "@/lib/csv";
import { firstRelation } from "@/lib/relations";
import {
  authoritativeShippedAt,
  shipmentCarrierLabel,
  shipmentOperatorStatus,
  shipmentPostageLabel,
} from "@/lib/warehouse-projection";
import { australiaPostTrackingUrl, isAustraliaPostShipment } from "@/lib/shipment-timeline";
import { fulfilmentShipmentPath } from "@/lib/fulfilment-workspace";

interface ShipmentRow {
  id: string;
  fulfilment_id?: string;
  status: string;
  tracking_number: string | null;
  shipped_at: string | null;
  delivered_at: string | null;
  currency: string;
  shipping_cost: number | null;
  shipping_provider?: string | null;
  service_name?: string | null;
  provider_sync_status?: string | null;
  provider_shipment_id?: string | null;
  carrier_reference?: string | null;
  label_url?: string | null;
  label_type?: string | null;
  tracking_recorded_at?: string | null;
  carrier_services?: { name: string; carrier_code: string } | null;
  fulfilments?: { fulfilment_number: string; sales_orders?: { order_number: string } | null } | null;
}

const SHIPMENT_STATUSES = ["pending", "pending_carrier", "packed", "ready", "shipped", "in_transit", "delivered", "exception", "cancelled"];
const sanitize = (q: string) => q.replace(/[,%_()]/g, "").trim();

export default function Shipments({ embedded = false }: { embedded?: boolean }) {
  const { org } = useAuth();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["shipments", org?.id, search, statusFilter, page, pageSize],
    enabled: !!org?.id,
    queryFn: async () => {
      let q = supabase
        .from("shipments")
        .select(
          "id, fulfilment_id, status, tracking_number, shipped_at, delivered_at, currency, shipping_cost, shipping_provider, service_name, provider_sync_status, provider_shipment_id, carrier_reference, label_url, label_type, carrier_services(name, carrier_code), fulfilments(fulfilment_number, sales_orders(order_number))",
          { count: "exact" }
        )
        .eq("org_id", org!.id);
      const term = sanitize(search);
      if (term) {
        const ilike = `%${term}%`;
        const { data: matchedOrders } = await supabase
          .from("sales_orders")
          .select("id")
          .eq("org_id", org!.id)
          .ilike("order_number", ilike)
          .limit(100);
        const orderIds = ((matchedOrders ?? []) as { id: string }[]).map((o) => o.id);
        if (orderIds.length) {
          const { data: matchedFulfilments } = await supabase
            .from("fulfilments")
            .select("id")
            .eq("org_id", org!.id)
            .in("sales_order_id", orderIds)
            .limit(200);
          const fulfilmentIds = ((matchedFulfilments ?? []) as { id: string }[]).map((f) => f.id);
          if (fulfilmentIds.length) {
            q = q.or(`tracking_number.ilike.${ilike},fulfilment_id.in.(${fulfilmentIds.join(",")})`);
          } else {
            q = q.ilike("tracking_number", ilike);
          }
        } else {
          q = q.ilike("tracking_number", ilike);
        }
      }
      if (statusFilter) q = q.eq("status", statusFilter);
      q = q.order("created_at", { ascending: false }).range(page * pageSize, page * pageSize + pageSize - 1);
      const { data, count } = await q;
      const ids = (data ?? []).map((row) => row.id);
      const events = ids.length
        ? await supabase
            .from("tracking_events")
            .select("shipment_id, occurred_at, event_code")
            .eq("org_id", org!.id)
            .in("shipment_id", ids)
            .eq("event_code", "TRACKING_RECORDED")
        : { data: [] };
      const recordedAt: Record<string, string> = {};
      for (const ev of events.data ?? []) {
        const row = ev as { shipment_id: string; occurred_at: string | null };
        if (row.occurred_at && !recordedAt[row.shipment_id]) recordedAt[row.shipment_id] = row.occurred_at;
      }
      return {
        rows: (data ?? []).map((row) => {
          const fulfilment = firstRelation(row.fulfilments);
          return {
            ...row,
            tracking_recorded_at: recordedAt[row.id] ?? null,
            carrier_services: firstRelation(row.carrier_services),
            fulfilments: fulfilment
              ? {
                  ...fulfilment,
                  sales_orders: firstRelation(fulfilment.sales_orders),
                }
              : null,
          };
        }) as ShipmentRow[],
        total: count ?? 0,
      };
    },
  });

  const handleExport = async () => {
    let q = supabase
      .from("shipments")
      .select("id, status, tracking_number, shipped_at, delivered_at, currency, shipping_cost, carrier_services(name, carrier_code), fulfilments(fulfilment_number, sales_orders(order_number))")
      .eq("org_id", org!.id);
    if (statusFilter) q = q.eq("status", statusFilter);
    const term = sanitize(search);
    if (term) q = q.ilike("tracking_number", `%${term}%`);
    q = q.order("created_at", { ascending: false });
    const { data } = await q;
    const rows = (data ?? []).map((row) => {
      const fulfilment = firstRelation(row.fulfilments);
      return {
        ...row,
        carrier_services: firstRelation(row.carrier_services),
        fulfilments: fulfilment
          ? {
              ...fulfilment,
              sales_orders: firstRelation(fulfilment.sales_orders),
            }
          : null,
      };
    }) as ShipmentRow[];
    const headers = ["tracking_number", "order", "carrier", "status", "shipping_cost", "currency", "shipped_at", "delivered_at"];
    downloadCsv(
      `shipments-${new Date().toISOString().slice(0, 10)}.csv`,
      toCsv(headers, rows.map((s) => [
        s.tracking_number ?? "",
        s.fulfilments?.sales_orders?.order_number ?? "",
        s.carrier_services?.name ?? "",
        s.status,
        s.shipping_cost ?? "",
        s.currency,
        s.shipped_at ?? "",
        s.delivered_at ?? "",
      ])),
    );
  };

  return (
    <div>
      {!embedded && (
      <PageHeader title="Shipments" subtitle="Carrier movements, postage and tracking across fulfilments">
        <button onClick={handleExport} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg transition-colors whitespace-nowrap flex items-center gap-2">
          <i className="ri-download-2-line" /> Export
        </button>
      </PageHeader>
      )}
      {embedded && (
        <div className="mb-3 flex justify-end">
          <button onClick={handleExport} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg transition-colors whitespace-nowrap flex items-center gap-2">
            <i className="ri-download-2-line" /> Export
          </button>
        </div>
      )}

      {isLoading ? (
        <TableSkeleton />
      ) : isError ? (
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
          <p className="text-sm text-slate-500 mb-4">Couldn't load shipments.</p>
          <button onClick={() => refetch()} className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium rounded-lg whitespace-nowrap">
            Retry
          </button>
        </div>
      ) : !data || data.total === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200">
          <EmptyState icon="ri-box-3-line" title="No shipments" description="Fulfil a sales order and create a shipment to start the postage and tracking workflow." />
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="p-4 border-b border-slate-100 flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1 max-w-md">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm" />
              <input
                type="text"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                placeholder="Search by tracking or order number..."
                className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
              />
            </div>
            <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }} className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm">
              <option value="">All statuses</option>
              {SHIPMENT_STATUSES.map((s) => <option key={s} value={s}>{statusLabel(s)}</option>)}
            </select>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50/70 border-b border-slate-100">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Tracking #</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Order</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Carrier</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Postage</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Shipped</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Track</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {(data.rows ?? []).map((s) => {
                  const trackUrl = isAustraliaPostShipment(s) ? australiaPostTrackingUrl(s.tracking_number) : null;
                  return (
                  <tr key={s.id} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors cursor-pointer" onClick={() => navigate(fulfilmentShipmentPath(s.id))}>
                    <td className="px-4 py-3 text-sm font-mono font-medium text-slate-800">{s.tracking_number ?? "—"}</td>
                    <td className="px-4 py-3 text-sm text-slate-600 font-mono">{s.fulfilments?.sales_orders?.order_number ?? "—"}</td>
                    <td className="px-4 py-3 text-sm text-slate-600">{shipmentCarrierLabel(s)}</td>
                    <td className="px-4 py-3">
                      <StatusBadge status={shipmentOperatorStatus(s).badge} customLabel={shipmentOperatorStatus(s).label} />
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-700 text-right">{shipmentPostageLabel(s)}</td>
                    <td className="px-4 py-3 text-sm text-slate-600">{fmtDateTime(authoritativeShippedAt(s))}</td>
                    <td className="px-4 py-3 text-sm">
                      {trackUrl ? (
                        <a
                          href={trackUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="font-medium text-emerald-600 hover:text-emerald-700 whitespace-nowrap"
                        >
                          Track on Australia Post
                        </a>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(fulfilmentShipmentPath(s.id));
                        }}
                        className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-medium rounded-lg whitespace-nowrap"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <Pagination page={page} pageSize={pageSize} total={data.total} onPageChange={setPage} onPageSizeChange={(s) => { setPageSize(s); setPage(0); }} />
        </div>
      )}
    </div>
  );
}
